import { useMemo, useCallback, ReactElement } from 'react'
import { Route, Navigate, useLocation } from 'react-router-dom'
import { useFeatureFlagContext } from '@app/helpers/hooks'
import { FeatureFlagName } from '@app/services/featureFlags/types'
import { useSnapshot } from 'valtio'
import { UserState } from '@app/storage/user'
import { Role } from '@app/types/user'

type Location = ReturnType<typeof useLocation>

type GuardedRouteProps = {
  path?: string
  featureFlagNames?: FeatureFlagName[]
  roles?: Role[] | ((location: Location) => Role[])
  redirectPath?: string | ((location: Location) => string)
  children?: ReactElement | null
}

export const GuardedRoute: React.FC<GuardedRouteProps> = ({
  featureFlagNames,
  roles = [],
  redirectPath = '/',
  children,
  path,
}) => {
  const { user } = useSnapshot(UserState)
  const { featureFlags } = useFeatureFlagContext()

  // Check feature flags
  const isRouteDisabledByFeatureFlag = useMemo(
    () =>
      !!featureFlagNames?.length &&
      featureFlagNames.some(flagName => !featureFlags[flagName]),
    [featureFlagNames, featureFlags]
  )

  // Check roles
  const isRouteDisabledByRole = useCallback(
    (currentLocation: Location) => {
      const targetRoles = Array.isArray(roles) ? roles : roles(currentLocation)
      return !(
        user?.role === 'Super Administrator' ||
        targetRoles.includes(user?.role)
      )
    },
    [roles, user?.role]
  )

  const GuardedElement = () => {
    const location = useLocation()
    if (
      isRouteDisabledByRole(location) ||
      isRouteDisabledByFeatureFlag
    ) {
      const targetRedirectPath =
        typeof redirectPath === 'string'
          ? redirectPath
          : redirectPath(location)

      return <Navigate to={targetRedirectPath} replace state={{ from: location }} />
    }

    return <>{children}</>
  }

  return (
    <Route
      path={path}
      element={<GuardedElement />}
    />
  )
}

